import React, { Component, ErrorInfo, ReactNode } from 'react';
import { RenderRequest } from 'types/cx-editor';
import { Box, Typography, useTheme, Card, CardHeader, Button } from '@mui/material';
import { useWidgetSdk } from 'context/WidgetSdkContext';
import { WidgetErrorBoundary } from './WidgetErrorBoundary';
import { MobilePreview } from './MobilePreview';
import { WebWidgetMobileIframe } from './WebWidgetMobileIframe';
import { initializeAndRenderWidget, initializeAndRenderMultipleWidgets } from 'utils/widget-renderer';
import { getWidgetIdFromTemplate, getWebWidgetDesktopWidth, getWebWidgetDisplayName, isMultiWidgetTemplate } from 'constants/web-widget-mapping';
import { useSelector } from 'react-redux';
import { RootState } from 'store/Store';
import { selectSelectedPilotId } from 'store/dataReducers/pilotsSlice';
import { selectProjectAttributes } from 'store/dataReducers/applicationScopeSlice';
import { selectScenariosForTemplate, selectDataScenariosLoading } from 'store/dataReducers/dataScenariosSlice';
import { toast } from 'utils/toast';

/** iPhone 17 Pro Max frame (440×956 viewport, 14px bezel). */
const MOBILE_PHONE = { width: 440, height: 956, bezel: 14 } as const;

/** Max scale so the phone never looks oversized on large screens (more like a real iPhone). */
const MAX_PHONE_SCALE = 0.7;

/** Mobile viewport CSS: forces entire page to fit inside device frame, prevents horizontal overflow. */
const EMAIL_MOBILE_CSS = `
  *, *::before, *::after { box-sizing: border-box !important; }
  html {
    width: 100% !important;
    min-width: 0 !important;
    max-width: 100% !important;
    height: 100%;
    margin: 0;
    padding: 0;
    overflow-x: hidden !important;
    overflow-y: auto !important;
    -webkit-text-size-adjust: 100%;
    -webkit-overflow-scrolling: touch;
    -ms-overflow-style: -ms-autohiding-scrollbar;
  }
  body {
    margin: 0 !important;
    padding: 0 !important;
    padding-top: 44px !important;
    width: 100% !important;
    min-width: 0 !important;
    max-width: 100% !important;
    min-height: 100%;
    overflow-x: hidden !important;
    overflow-y: visible;
    overflow-wrap: break-word !important;
    word-wrap: break-word !important;
    word-break: break-word;
    /* Do not set font-family: email inline styles (Arial, Poppins, etc.) must win to avoid different line breaks */
  }
  body > * {
    max-width: 100% !important;
    min-width: 0 !important;
  }
  img {
    max-width: 100% !important;
    width: auto !important;
    height: auto !important;
    display: block;
  }
  table, table[width] {
    max-width: 100% !important;
    width: 100% !important;
    min-width: 0 !important;
  }
  td, th, td[width], th[width] {
    max-width: 100% !important;
    overflow-wrap: break-word !important;
    word-wrap: break-word !important;
    word-break: break-word;
  }
  .wrapper, .outer, .webkit, .email-container, .content,
  center[class="wrapper"], div[class="webkit"], table[class="outer"],
  table[class="wrapper"], div[class="outer"], div[class="wrapper"],
  [class*="wrapper"], [class*="container"], [class*="content"],
  table[width], td[width], th[width] {
    max-width: 100% !important;
    min-width: 0 !important;
  }
  pre, code {
    max-width: 100% !important;
    overflow-x: auto !important;
    white-space: pre-wrap !important;
    word-wrap: break-word !important;
  }
  a, button, input, select, textarea {
    max-width: 100% !important;
  }
  div, section, article, main, aside, header, footer, span, p {
    max-width: 100% !important;
    min-width: 0 !important;
  }
  [style*="flex"], [style*="display: flex"], .flex, [class*="flex"] {
    flex-wrap: wrap !important;
    max-width: 100% !important;
    min-width: 0 !important;
  }
  [style*="flex"] > *, [style*="display: flex"] > *, .flex > *, [class*="flex"] > * {
    min-width: 0 !important;
    flex-shrink: 1 !important;
  }

  /* User Feedback (FEEDBACK_V4, FEEDBACK_V3, FEEDBACK): keep question + icons on same line */
  tr[id$="FEEDBACK_V4"] div,
  tr[id$="FEEDBACK_V4"] table,
  tr[id$="FEEDBACK_V3"] div,
  tr[id$="FEEDBACK_V3"] table,
  tr[id$="FEEDBACK"] div,
  tr[id$="FEEDBACK"] table {
    max-width: 100% !important;
    width: 100% !important;
    box-sizing: border-box !important;
  }
  tr[id$="FEEDBACK_V4"] tr,
  tr[id$="FEEDBACK_V3"] tr,
  tr[id$="FEEDBACK"] tr {
    display: table-row !important;
  }
  tr[id$="FEEDBACK_V4"] td,
  tr[id$="FEEDBACK_V3"] td,
  tr[id$="FEEDBACK"] td {
    display: table-cell !important;
    vertical-align: middle !important;
    box-sizing: border-box !important;
    padding: 12px 16px 0 !important;
  }
  tr[id$="FEEDBACK_V4"] td:first-of-type,
  tr[id$="FEEDBACK_V3"] td:first-of-type,
  tr[id$="FEEDBACK"] td:first-of-type {
    white-space: normal !important;
    width: auto !important;
    min-width: 0 !important;
  }
  tr[id$="FEEDBACK_V4"] td:not(:first-of-type),
  tr[id$="FEEDBACK_V3"] td:not(:first-of-type),
  tr[id$="FEEDBACK"] td:not(:first-of-type) {
    white-space: nowrap !important;
    width: 110px !important;
    max-width: 110px !important;
  }
  tr[id$="FEEDBACK_V4"] div,
  tr[id$="FEEDBACK_V3"] div,
  tr[id$="FEEDBACK"] div {
    padding-left: 16px !important;
    padding-bottom: 12px !important;
  }
  /* Thumbs icons: prevent clipping, ensure full display */
  tr[id$="FEEDBACK_V4"] td:not(:first-of-type) img,
  tr[id$="FEEDBACK_V3"] td:not(:first-of-type) img,
  tr[id$="FEEDBACK"] td:not(:first-of-type) img {
    display: inline-block !important;
    vertical-align: middle !important;
    object-fit: contain !important;
    width: 24px !important;
    height: 24px !important;
    max-width: 24px !important;
    max-height: 24px !important;
  }
  tr[id$="FEEDBACK_V4"] td:not(:first-of-type),
  tr[id$="FEEDBACK_V3"] td:not(:first-of-type),
  tr[id$="FEEDBACK"] td:not(:first-of-type) {
    overflow: visible !important;
    min-height: 46px !important;
  }

  /* Footer (FOOTER, FOOTER_V3, FOOTER_V4, NEW_FOOTER): prevent overflow, reduce extra space */
  tr[id$="FOOTER"] table,
  tr[id$="FOOTER_V3"] table,
  tr[id$="FOOTER_V4"] table,
  tr[id$="NEW_FOOTER"] table {
    max-width: 100% !important;
    box-sizing: border-box !important;
  }
  tr[id$="FOOTER"] > td,
  tr[id$="FOOTER_V3"] > td,
  tr[id$="FOOTER_V4"] > td,
  tr[id$="NEW_FOOTER"] > td {
    max-width: 100% !important;
    box-sizing: border-box !important;
    padding: 20px 16px 16px !important;
    height: auto !important;
  }
  tr[id$="FOOTER"] .content-block-footer,
  tr[id$="FOOTER_V3"] .content-block-footer,
  tr[id$="FOOTER_V4"] .content-block-footer,
  tr[id$="NEW_FOOTER"] .content-block-footer {
    padding-top: 20px !important;
    padding-bottom: 16px !important;
  }
  tr[id$="FOOTER"] .table-not-adapt,
  tr[id$="FOOTER"] table[class*="social"],
  tr[id$="FOOTER_V3"] .table-not-adapt,
  tr[id$="FOOTER_V3"] table[class*="social"],
  tr[id$="FOOTER_V4"] .table-not-adapt,
  tr[id$="FOOTER_V4"] table[class*="social"],
  tr[id$="NEW_FOOTER"] .table-not-adapt,
  tr[id$="NEW_FOOTER"] table[class*="social"] {
    margin: 16px 0 !important;
  }

  /* Bill Summary (MONTHLY_SUMMARY_V2, MONTHLY_SUMMARY): align arrow with main amount */
  tr[id$="MONTHLY_SUMMARY_V2"] table tr td,
  tr[id$="MONTHLY_SUMMARY"] table tr td {
    vertical-align: bottom !important;
  }
  /* Bill Summary: keep amount column (and green row) on one line — target inner table where amounts live */
  tr[id$="MONTHLY_SUMMARY_V2"] table tr td:last-child,
  tr[id$="MONTHLY_SUMMARY"] table tr td:last-child,
  tr[id$="MONTHLY_SUMMARY_V2"] table table tr td:last-child,
  tr[id$="MONTHLY_SUMMARY"] table table tr td:last-child {
    white-space: nowrap !important;
  }
  /* All section tables: prevent numbers from breaking mid-digit (Bill Summary, Itemization, etc.) */
  tr[id] table td,
  tr[id] table th {
    word-break: normal !important;
    overflow-wrap: normal !important;
  }
  tr[id$="MONTHLY_SUMMARY_V2"] img[src*="Up_down"],
  tr[id$="MONTHLY_SUMMARY_V2"] img[alt*="arrow"],
  tr[id$="MONTHLY_SUMMARY_V2"] img[alt*="Up"],
  tr[id$="MONTHLY_SUMMARY_V2"] img[alt*="Down"],
  tr[id$="MONTHLY_SUMMARY"] img[src*="Up_down"],
  tr[id$="MONTHLY_SUMMARY"] img[alt*="arrow"],
  tr[id$="MONTHLY_SUMMARY"] img[alt*="Up"],
  tr[id$="MONTHLY_SUMMARY"] img[alt*="Down"] {
    vertical-align: middle !important;
    display: inline-block !important;
  }

  /* All section rows: ensure nested tables/divs respect viewport (safety for HEADER, GREETING, MONTHLY_SUMMARY, ITEMIZATION, etc.) */
  tr[id] table,
  tr[id] div {
    max-width: 100% !important;
    box-sizing: border-box !important;
  }
`;

class EmailPreviewErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean; error: Error | null }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[EmailPreviewErrorBoundary] Email render error:', error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box sx={{ padding: 3, textAlign: 'center', bgcolor: 'error.light', borderRadius: 2, border: 1, borderColor: 'error.main' }}>
          <Typography variant="h6" color="error" gutterBottom>Email Preview Error</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {this.state.error?.message || 'An error occurred while rendering the email preview'}
          </Typography>
          <Button variant="outlined" color="error" onClick={() => this.setState({ hasError: false, error: null })}>
            Try Again
          </Button>
        </Box>
      );
    }
    return this.props.children;
  }
}

/**
 * Builds a full HTML document string for rendering email content in an iframe
 * for desktop preview. Passes through the full document as-is so the email's
 * own <style> tags and inline font-family declarations are preserved.
 * Intentionally adds NO font or layout overrides so the rendering matches
 * production email clients (avoids the global `* { font-family: Inter }` rule
 * from index.css leaking in through dangerouslySetInnerHTML).
 */
function buildEmailDesktopDocument(html: string): string {
  if (!html?.trim()) {
    return `<!DOCTYPE html><html><head><meta charset="UTF-8"></head><body></body></html>`;
  }
  const trimmed = html.trim();
  const looksLikeFullDocument =
    trimmed.toLowerCase().startsWith('<!doctype') || trimmed.toLowerCase().startsWith('<html');
  // For full documents pass through as-is. The iframe srcdoc is already sandboxed so
  // the app's global CSS cannot bleed in — there is no reason to reconstruct the document.
  // Reconstructing with DOMParser drops <html>/<body> attributes, MSO conditional comments,
  // non-style <meta> tags, and overwrites original body styles, all of which break email rendering.
  if (looksLikeFullDocument) {
    return html;
  }
  // Fragment: wrap in a minimal shell.
  return `<!DOCTYPE html><html><head><meta charset="UTF-8"></head><body style="margin:0;padding:0;">${html}</body></html>`;
}

function extractEmailTeaserText(html: string): string | null {
  if (!html?.trim()) return null;

  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const nodes = Array.from(doc.querySelectorAll('div, span, p, td')).reverse();

    for (const el of nodes) {
      const style = (el.getAttribute('style') || '').replace(/\s+/g, '').toLowerCase();
      const isHidden =
        style.includes('mso-hide:all') ||
        style.includes('display:none') ||
        style.includes('visibility:hidden') ||
        style.includes('max-height:0') ||
        style.includes('max-width:0') ||
        style.includes('font-size:0') ||
        style.includes('line-height:0') ||
        style.includes('opacity:0');

      if (!isHidden) continue;

      const text = (el.textContent || '').replace(/\s+/g, ' ').trim();
      if (text) return text;
    }
  } catch {
    return null;
  }

  return null;
}

/** Desktop email preview: auto-height iframe so global app CSS cannot affect email font rendering. */
const EmailDesktopPreview: React.FC<{ html: string }> = ({ html }) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const iframeRef = React.useRef<HTMLIFrameElement>(null);
  const cleanupRef = React.useRef<(() => void) | null>(null);
  const [iframeSize, setIframeSize] = React.useState({ width: 1024, height: 600 });
  const [scale, setScale] = React.useState(1);
  const [iframeKey, setIframeKey] = React.useState(0);

  const srcdoc = React.useMemo(() => buildEmailDesktopDocument(html), [html]);

  const measureIframe = React.useCallback(() => {
    const iframe = iframeRef.current;
    const doc = iframe?.contentDocument;
    const root = doc?.documentElement;
    const body = doc?.body;
    if (!root || !body) return;

    // Width: use the wider of root and body (email max-width containers sit on root).
    // Height: use body only — root.scrollHeight/clientHeight equals the iframe's current
    // viewport height (set via iframeSize.height), so including it would prevent the
    // height from ever shrinking below its previous value.
    const width = Math.max(
      root.scrollWidth,
      root.offsetWidth,
      root.clientWidth,
      body.scrollWidth,
      body.offsetWidth,
      body.clientWidth,
      1
    );
    const height = Math.max(
      body.scrollHeight,
      body.offsetHeight,
      body.clientHeight,
      1
    );

    setIframeSize(prev =>
      prev.width === width && prev.height === height ? prev : { width, height }
    );
  }, []);

  React.useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const updateScale = () => {
      const nextScale = Math.min(1, el.clientWidth / Math.max(iframeSize.width, 1));
      setScale(prev => (Math.abs(prev - nextScale) < 0.001 ? prev : nextScale));
    };

    updateScale();

    let rafId = 0;
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(updateScale);
    });
    ro.observe(el);

    return () => {
      cancelAnimationFrame(rafId);
      ro.disconnect();
    };
  }, [iframeSize.width]);

  React.useEffect(() => {
    cleanupRef.current?.();
    cleanupRef.current = null;
    setIframeSize({ width: 1024, height: 600 }); // Reset so new (possibly smaller) content measures correctly
    setIframeKey(k => k + 1); // Force iframe remount on new content so stale DOM state is discarded
  }, [srcdoc]);

  React.useEffect(() => {
    return () => cleanupRef.current?.();
  }, []);

  const handleLoad = React.useCallback(() => {
    cleanupRef.current?.();

    const iframe = iframeRef.current;
    const win = iframe?.contentWindow;
    const doc = iframe?.contentDocument;
    const root = doc?.documentElement;
    const body = doc?.body;

    if (!win || !doc || !root || !body) {
      measureIframe();
      return;
    }

    let rafId = 0;
    const scheduleMeasure = () => {
      win.cancelAnimationFrame(rafId);
      rafId = win.requestAnimationFrame(() => {
        measureIframe();
      });
    };

    scheduleMeasure();

    // Avoid ResizeObserver inside the iframe document: resizing the parent iframe in response
    // to observer callbacks can still create a feedback cycle and trigger the dev overlay
    // warning "ResizeObserver loop completed with undelivered notifications".
    // For email previews, observing DOM mutations plus image/font completion is enough.
    const mutationObserver =
      typeof MutationObserver !== 'undefined' ? new MutationObserver(scheduleMeasure) : null;
    mutationObserver?.observe(body, {
      attributes: true,
      childList: true,
      subtree: true,
      characterData: true,
    });

    const images = Array.from(doc.images);
    images.forEach(image => {
      image.addEventListener('load', scheduleMeasure);
      image.addEventListener('error', scheduleMeasure);
    });

    doc.fonts?.ready.then(scheduleMeasure).catch(() => {});
    win.setTimeout(scheduleMeasure, 0);

    cleanupRef.current = () => {
      win.cancelAnimationFrame(rafId);
      mutationObserver?.disconnect();
      images.forEach(image => {
        image.removeEventListener('load', scheduleMeasure);
        image.removeEventListener('error', scheduleMeasure);
      });
    };
  }, [measureIframe]);

  return (
    <Box
      ref={containerRef}
      sx={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          width: `${iframeSize.width * scale}px`,
          height: `${iframeSize.height * scale}px`,
          position: 'relative',
          flexShrink: 0,
        }}
      >
        <iframe
          key={iframeKey}
          ref={iframeRef}
          title="Email desktop preview"
          srcDoc={srcdoc}
          onLoad={handleLoad}
          style={{
            width: `${iframeSize.width}px`,
            height: `${iframeSize.height}px`,
            border: 'none',
            display: 'block',
            position: 'absolute',
            top: 0,
            left: 0,
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
            backgroundColor: '#ffffff',
          }}
        />
      </Box>
    </Box>
  );
};

/**
 * Builds a full HTML document string for rendering email content in an iframe
 * with a mobile viewport. If input has <html>/<body>, extracts body content
 * to avoid nested documents.
 */
function buildEmailMobileDocument(html: string, viewportWidth: number): string {
  if (!html?.trim()) {
    const meta = `<meta charset="UTF-8"><meta name="viewport" content="width=${viewportWidth}, initial-scale=1, maximum-scale=1, user-scalable=no">`;
    return `<!DOCTYPE html><html><head>${meta}</head><body></body></html>`;
  }
  const trimmed = html.trim();
  const looksLikeFullDocument =
    trimmed.toLowerCase().startsWith('<!doctype') || trimmed.toLowerCase().startsWith('<html');
  let bodyContent: string;
  let originalHeadStyles = '';
  if (looksLikeFullDocument) {
    try {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      bodyContent = doc.body?.innerHTML ?? doc.documentElement?.innerHTML ?? html;
      originalHeadStyles = Array.from(doc.head?.querySelectorAll('style, link[rel="stylesheet"]') ?? [])
        .map(el => el.outerHTML)
        .join('');
    } catch {
      bodyContent = html;
    }
  } else {
    bodyContent = html;
  }
  const meta = `<meta charset="UTF-8"><meta name="viewport" content="width=${viewportWidth}, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover">`;
  const style = `<style>${EMAIL_MOBILE_CSS}</style>`;
  return `<!DOCTYPE html><html><head>${meta}${originalHeadStyles}${style}</head><body>${bodyContent}</body></html>`;
}

/** Email mobile preview: iPhone 17 Pro Max frame with iframe rendering displayHtml. */
const EmailMobilePreview: React.FC<{ html: string }> = ({ html }) => {
  const theme = useTheme();
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [scale, setScale] = React.useState(1);
  const [iframeKey, setIframeKey] = React.useState(0);

  const srcdoc = React.useMemo(
    () => buildEmailMobileDocument(html, MOBILE_PHONE.width),
    [html]
  );

  // Force iframe remount on new content so stale DOM state is discarded
  React.useEffect(() => {
    setIframeKey(k => k + 1);
  }, [srcdoc]);

  const frameW = MOBILE_PHONE.width + MOBILE_PHONE.bezel * 2;
  const frameH = MOBILE_PHONE.height + MOBILE_PHONE.bezel * 2;

  React.useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const updateScale = () => {
      const { clientWidth, clientHeight } = el;
      const s = Math.min(clientWidth / frameW, clientHeight / frameH, MAX_PHONE_SCALE);
      setScale(Math.max(0.2, s));
    };
    updateScale();
    let rafId = 0;
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(updateScale);
    });
    ro.observe(el);
    return () => {
      cancelAnimationFrame(rafId);
      ro.disconnect();
    };
  }, [frameW, frameH]);

  return (
    <Box
      ref={containerRef}
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
      }}
    >
      {/* Wrapper with scaled dimensions for layout—transform:scale() does not reduce layout space */}
      <Box
        sx={{
          width: frameW * scale,
          height: frameH * scale,
          position: 'relative',
          overflow: 'hidden',
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            width: frameW,
            height: frameH,
            transform: `translate(-50%, -50%) scale(${scale})`,
            transformOrigin: 'center center',
            bgcolor: '#000',
            border: `${MOBILE_PHONE.bezel}px solid #000`,
            borderRadius: '44px',
            boxShadow:
              theme.palette.mode === 'dark'
                ? '0 20px 25px rgba(0,0,0,0.6)'
                : '0 20px 25px rgba(0,0,0,0.4)',
            overflow: 'hidden',
          }}
        >
        <Box
          sx={{
            width: MOBILE_PHONE.width,
            height: MOBILE_PHONE.height,
            bgcolor: 'background.paper',
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          {/* Dynamic Island - iPhone 17 Pro Max style */}
          <Box
            sx={{
              position: 'absolute',
              top: 12,
              left: '50%',
              transform: 'translateX(-50%)',
              width: 100,
              height: 28,
              borderRadius: 14,
              bgcolor: '#000',
              zIndex: 2,
            }}
          />
          <iframe
            key={iframeKey}
            title="Email mobile preview"
            srcDoc={srcdoc}
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
              backgroundColor: '#fff',
              display: 'block',
            }}
          />
        </Box>
        </Box>
      </Box>
    </Box>
  );
};

type VariantConfig = {
  templateKey?: string;
  hierarchy?: string;
  locale?: string;
  scenario?: string;
};

type PreviewFrameProps = {
  variantName?: string;
  variant?: VariantConfig | null;
  renderRequest: RenderRequest | null;
  previewMode: 'desktop' | 'mobile';
  channel?: string;
  desktopPreviewMaxWidth?: number;
  isFullTemplateSelected?: boolean;
  renderedHtml?: string;
  renderedSubject?: string;
  loading?: boolean;
  isWebWidgetTemplate?: boolean;
  herPreviewUrl?: string | null; // HER preview URL to display in iframe
  onWebWidgetFinished?: (templateKey: string, result: { success: boolean; error?: string }) => void;
};

export const PreviewFrame: React.FC<PreviewFrameProps> = ({
  variantName,
  variant,
  renderRequest,
  previewMode,
  channel = 'Email',
  desktopPreviewMaxWidth = 1024,
  isFullTemplateSelected = false,
  renderedHtml,
  renderedSubject,
  loading = false,
  isWebWidgetTemplate = false,
  herPreviewUrl,
  onWebWidgetFinished,
}) => {
  const theme = useTheme();
  const effectiveTemplateKey = variant?.templateKey || renderRequest?.templateKey;
  const desktopPreviewWidth = channel === 'Web' ? undefined : desktopPreviewMaxWidth;
  const refreshKey = renderRequest?.refreshKey ?? null; // Extract refreshKey to trigger re-render on save
  const effectiveElementKey = renderRequest?.elementKey || null;
  const { instanceId, instanceIdRef, isInitializing, initError, isReady, reinitialize, sdkScriptUrl, getIframeInitConfig } = useWidgetSdk();

  const widgetContainerRef = React.useRef<HTMLDivElement | null>(null);
  // Important: this page can render multiple widgets at once (e.g., 5 predefined widgets).
  // A static id like "widget-container" will collide and can break SDKs that query by element id.
  const reactId = React.useId();
  const widgetContainerDomId = React.useMemo(() => {
    // useId() can include ":" which is valid in HTML but can be awkward in selectors; normalize it.
    const rid = reactId.replace(/[:]/g, '');
    return `widget-container-${rid}`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reactId]);
  const [selectedWidgetTag, setSelectedWidgetTag] = React.useState<string | null>(null);
  const errorToastShownRef = React.useRef<string | null>(null); // Track which template we've shown error for

  // Resolve widget tag(s) for Web mobile iframe (SDK must be loaded so WIDGET_TAG is available).
  // Returns string for single widget, or array of strings for multi-widget templates
  const webMobileWidgetTag = React.useMemo(() => {
    if (!effectiveTemplateKey || typeof window === 'undefined') return null;
    const key = getWidgetIdFromTemplate(effectiveTemplateKey);
    const tags = (window as any).BidgelyWebSdk?.WIDGET_TAG as Record<string, string> | undefined;

    // For multi-widget templates, return array of all widget tags
    if (Array.isArray(key)) {
      if (!tags) return null;
      const resolvedTags = key.map(k => tags[k]).filter(Boolean);
      return resolvedTags.length > 0 ? resolvedTags : null;
    }

    return key && tags ? tags[key] ?? null : null;
  }, [effectiveTemplateKey, isReady]); // isReady so we recompute when SDK loads
  
  // Track template changes, refreshKey, scenario, and previewMode for re-initialization
  const prevTemplateKeyRef = React.useRef<string | null>(null);
  const prevRefreshKeyRef = React.useRef<number | null>(null);
  const prevScenarioIdRef = React.useRef<string | null>(null);
  const prevPreviewModeRef = React.useRef<'desktop' | 'mobile' | null>(null);
  /** When true, we have started init+render (reinitialize then render); skip effect re-runs until that call settles to avoid double init/render. */
  const initRenderInProgressRef = React.useRef(false);

  // Get data from Redux
  const selectedPilotId = useSelector(selectSelectedPilotId);
  const projectAttributes = useSelector(selectProjectAttributes);
  
  // Extract fuelType from projectAttributes
  const fuelType = React.useMemo(() => {
    let fuelType = 'ELECTRIC'; // Default fallback
    if (projectAttributes?.fuel_type) {
      if (typeof projectAttributes.fuel_type === 'string') {
        const fuelTypes = projectAttributes.fuel_type.split(',').map(f => f.trim());
        fuelType = fuelTypes[0]?.toUpperCase() || 'ELECTRIC';
      } else if (Array.isArray(projectAttributes.fuel_type)) {
        const fuelTypeArray = projectAttributes.fuel_type as any[];
        if (fuelTypeArray.length > 0) {
          fuelType = String(fuelTypeArray[0]).toUpperCase();
        }
      }
    }
    return fuelType;
  }, [projectAttributes]);

  const locale = variant?.locale || renderRequest?.variant?.locale || 'en_US';

  // Extract fuelType from renderRequest (for Web templates) or use default
  const requestFuelType = renderRequest?.fuelType;

  // Get scenarios from Redux
  const availableScenarios = useSelector((state: RootState) => {
    if (!effectiveTemplateKey || !selectedPilotId) {
      return [];
    }

    // Use request fuel type if available (from Web templates), otherwise use default fuel type
    const effectiveFuelType = requestFuelType || fuelType;

    console.log('[PreviewFrame] Fetching scenarios from Redux:', {
      templateKey: effectiveTemplateKey,
      effectiveFuelType,
      requestFuelType,
      fuelType,
      locale,
      channel,
      selectedPilotId
    });

    const scenarios = selectScenariosForTemplate(
      state,
      String(selectedPilotId),
      effectiveFuelType,
      locale,
      effectiveTemplateKey, // Clean template key
      channel
    );

    console.log('[PreviewFrame] Scenarios fetched from Redux:', {
      count: scenarios?.length || 0,
      scenarios: scenarios?.map(s => ({ scenarioId: s.scenarioId, templateName: s.templateName, fuelType: s.fuelType }))
    });

    return scenarios;
  });

  // Resolve scenario ID: always return an actual scenarioId from DataScenario objects
  // Note: 'default' is just a display name, not a scenarioId
  // availableScenarios are already filtered by template and productType (channel) in the selector
  const resolvedScenarioId = React.useMemo(() => {
    const scenario = variant?.scenario || renderRequest?.variant?.scenario;

    console.log('[PreviewFrame] Resolving scenario:', {
      effectiveTemplateKey,
      requestFuelType,
      scenario,
      availableScenariosCount: availableScenarios?.length || 0,
      availableScenarios: availableScenarios?.map(s => ({ scenarioId: s.scenarioId, templateName: s.templateName }))
    });

    // Special-case: "Web_FullTemplate_1" is a cross-template Web scenario used for rendering multiple widgets
    // (e.g., Color Palette "View All Web Widgets"). It may not appear under each widget's templateName in scenarios,
    // so we must allow it even if it's not present in availableScenarios for the specific widget template.
    if (channel === 'Web' && scenario === 'Web_FullTemplate_1') {
      return scenario;
    }

    // For any channel (including Web): only use an explicitly provided scenario
    // if it exists in the scenarios list for this template. Otherwise, fall back
    // to the template's default scenario to avoid crashing the widget runtime.
    if (scenario && scenario !== 'default') {
      const foundScenario = availableScenarios?.find(ds => ds.scenarioId === scenario);
      if (foundScenario) {
        return foundScenario.scenarioId;
      }
      // Fall through to default scenario resolution
    }

    // If no scenarios available, we cannot resolve a scenarioId
    if (!availableScenarios || availableScenarios.length === 0) {
      console.error('[PreviewFrame] No scenarios available - cannot resolve scenarioId');
      return null; // Return null instead of 'default' string
    }

    // Note: scenario override (if present) is handled above with a safe existence check.

    // Find default scenario: first try defaultScenario flag, then scenarioDisplayName "Default", then first scenario
    // This handles both "default" string and when no scenario is provided
    const defaultScenario = availableScenarios.find(ds => ds.defaultScenario === true) ||
                           availableScenarios.find(ds => ds.scenarioDisplayName === 'Default') ||
                           availableScenarios[0];

    if (defaultScenario) {
      return defaultScenario.scenarioId; // Always return actual scenarioId, never 'default' string
    }

    // This should never happen if availableScenarios has items
    console.error('[PreviewFrame] Could not find any scenario in availableScenarios');
    return null;
  }, [variant?.scenario, renderRequest?.variant?.scenario, availableScenarios, channel, effectiveTemplateKey, requestFuelType]);

  // Get loading state to avoid showing error while scenarios are still loading
  const scenariosLoading = useSelector((state: RootState) => {
    if (!selectedPilotId || !effectiveTemplateKey) return false;

    // Use request fuel type if available
    const effectiveFuelType = requestFuelType || fuelType;

    return selectDataScenariosLoading(
      state,
      String(selectedPilotId),
      effectiveFuelType,
      locale
    );
  });

  // Show error toast when no data scenario is found for Web widget
  // Use a delayed approach to avoid showing toast during rapid state changes
  React.useEffect(() => {
    // Only show error for Web widgets when:
    // 1. Template is selected
    // 2. Scenarios are not loading
    // 3. SDK is ready (to avoid showing during initial load)
    // 4. No scenarios available
    if (
      channel === 'Web' &&
      isWebWidgetTemplate &&
      effectiveTemplateKey &&
      !scenariosLoading &&
      isReady && // Only show when SDK is ready
      (!availableScenarios || availableScenarios.length === 0)
    ) {
      // Only show toast once per template to avoid spam
      if (errorToastShownRef.current !== effectiveTemplateKey) {
        // Add a delay to avoid showing toast during rapid state changes or initial load
        // This ensures we only show the error when the user is actually trying to view the widget
        const timeoutId = setTimeout(() => {
          // Double-check conditions after delay to ensure we still need to show the error
          if (
            channel === 'Web' &&
            isWebWidgetTemplate &&
            effectiveTemplateKey &&
            !scenariosLoading &&
            isReady &&
            (!availableScenarios || availableScenarios.length === 0) &&
            errorToastShownRef.current !== effectiveTemplateKey
          ) {
        errorToastShownRef.current = effectiveTemplateKey;
            // Use warning instead of error to be less aggressive
            toast.warning(
              `No data scenario found for widget "${effectiveTemplateKey}". Please configure a data scenario in the Data Scenarios section.`
        );
          }
        }, 2000); // Wait 2 seconds before showing toast to ensure stable state

        return () => {
          clearTimeout(timeoutId);
        };
      }
    } else if (availableScenarios && availableScenarios.length > 0) {
      // Reset the ref when scenarios become available
      errorToastShownRef.current = null;
    }
  }, [
    channel,
    isWebWidgetTemplate,
    effectiveTemplateKey,
    availableScenarios,
    scenariosLoading,
    isReady, // Add isReady to dependencies
  ]);

  // Render widget when SDK is ready and template is selected
  React.useEffect(() => {
    if (channel !== 'Web' || !isWebWidgetTemplate) {
      setSelectedWidgetTag(null);
      if (widgetContainerRef.current) {
        widgetContainerRef.current.innerHTML = '';
      }
      return;
    }

    if (!effectiveTemplateKey || !isReady || !instanceId || !resolvedScenarioId) {
      return;
    }

    // Skip if we already started an init+render (reinitialize then render): reinitialize() updates instanceId state and re-triggers this effect; we must not start a second init+render.
    if (initRenderInProgressRef.current) {
      return;
    }

    // Re-initialize whenever we need to render with fresh context: template, refreshKey, scenario, or preview mode. Init and render are mandatory together (always init before render).
    const templateChanged = effectiveTemplateKey !== prevTemplateKeyRef.current && prevTemplateKeyRef.current !== null;
    const refreshKeyChanged = refreshKey !== null && refreshKey !== prevRefreshKeyRef.current && prevRefreshKeyRef.current !== null;
    const scenarioChanged = prevScenarioIdRef.current !== null && prevScenarioIdRef.current !== resolvedScenarioId;
    const previewModeChanged = prevPreviewModeRef.current !== null && prevPreviewModeRef.current !== previewMode;
    const shouldReinitialize = templateChanged || refreshKeyChanged || scenarioChanged || previewModeChanged;

    // Web mobile uses iframe-based preview; widget is rendered inside the iframe. Trigger reinitialize with selected scenario so init uses same scenario (single uiConfigs call).
    if (previewMode === 'mobile') {
      if (shouldReinitialize) {
        // Use request fuel type for mobile preview
        reinitialize(resolvedScenarioId, requestFuelType || undefined).catch(err => console.error('[PreviewFrame] Re-initialize for mobile failed:', err));
      }
      return;
    }

    if (!widgetContainerRef.current) {
      return;
    }

    // Prevent duplicate renders for the same widget + scenario + instance + refreshKey in dev StrictMode
    const renderSignature = `${effectiveTemplateKey}|${instanceId}|${resolvedScenarioId}|${String(refreshKey ?? '')}`;
    const alreadyRendered =
      widgetContainerRef.current &&
      widgetContainerRef.current.childNodes &&
      widgetContainerRef.current.childNodes.length > 0;
    if (prevScenarioIdRef.current === resolvedScenarioId && prevTemplateKeyRef.current === effectiveTemplateKey && prevRefreshKeyRef.current === refreshKey && alreadyRendered) {
      return;
    }

    // Mandatory: init then render together. When we reinitialize, mark in progress so effect re-runs (from instanceId updates) don't start a second init+render.
    if (shouldReinitialize) {
      initRenderInProgressRef.current = true;
    }

    // Check if this is a multi-widget template
    const isMultiWidget = isMultiWidgetTemplate(effectiveTemplateKey);

    // Use appropriate renderer based on template type
    const renderPromise = isMultiWidget
      ? initializeAndRenderMultipleWidgets({
          templateKey: effectiveTemplateKey,
          scenarioId: resolvedScenarioId,
          instanceId: instanceId,
          instanceIdRef: shouldReinitialize ? instanceIdRef : undefined,
          containerRef: widgetContainerRef,
          reinitialize: shouldReinitialize ? () => reinitialize(resolvedScenarioId, requestFuelType || undefined) : undefined,
          onRenderComplete: (response) => {
            console.log('[PreviewFrame] Multi-widget render complete:', response);
          },
          onError: (error) => {
            console.error('[PreviewFrame] Multi-widget render error:', error);
            if (widgetContainerRef.current) {
              widgetContainerRef.current.innerHTML = '';
            }
          }
        })
      : initializeAndRenderWidget({
          templateKey: effectiveTemplateKey, // Clean template key
          scenarioId: resolvedScenarioId,
          instanceId: instanceId,
          instanceIdRef: shouldReinitialize ? instanceIdRef : undefined,
          containerRef: widgetContainerRef,
          reinitialize: shouldReinitialize ? () => reinitialize(resolvedScenarioId, requestFuelType || undefined) : undefined,
          onRenderComplete: (response) => {
            // Get widget tag for state tracking
            const widgetKey = getWidgetIdFromTemplate(effectiveTemplateKey);
            if (widgetKey && !Array.isArray(widgetKey) && window.BidgelyWebSdk?.WIDGET_TAG?.[widgetKey]) {
              setSelectedWidgetTag(window.BidgelyWebSdk.WIDGET_TAG[widgetKey]);
            }
          },
          onError: (error) => {
            console.error('[PreviewFrame] Widget render error:', error);
            // Clear container on error
            if (widgetContainerRef.current) {
              widgetContainerRef.current.innerHTML = '';
            }
          }
        });

    renderPromise
      .then((result) => {
        if (result.success) {
          if (result.widgetTag) {
            setSelectedWidgetTag(result.widgetTag);
          } else if (result.widgetTags && result.widgetTags.length > 0) {
            // For multi-widget, set the first widget tag
            setSelectedWidgetTag(result.widgetTags[0]);
          }
        }
        if (onWebWidgetFinished && effectiveTemplateKey) {
          onWebWidgetFinished(effectiveTemplateKey, { success: result.success, error: result.error });
        }
      })
      .catch((error) => {
        console.error('[PreviewFrame] Fatal error in widget rendering:', error);
        if (onWebWidgetFinished && effectiveTemplateKey) {
          onWebWidgetFinished(effectiveTemplateKey, { success: false, error: error?.message || String(error || '') });
        }
      })
      .finally(() => {
        initRenderInProgressRef.current = false;
      });

    // Cleanup function to remove widget on unmount or template change
    return () => {
      if (widgetContainerRef.current) {
        widgetContainerRef.current.innerHTML = '';
      }
      setSelectedWidgetTag(null);
    };
  }, [channel, isWebWidgetTemplate, effectiveTemplateKey, isReady, instanceId, resolvedScenarioId, refreshKey, reinitialize, previewMode]); // previewMode: re-render widget when switching desktop/mobile so the correct container (ref) gets the widget
  
  // Track template, refreshKey, scenario, and previewMode to update refs (for determining when to re-initialize)
  React.useEffect(() => {
    if (effectiveTemplateKey && effectiveTemplateKey !== prevTemplateKeyRef.current) {
      prevTemplateKeyRef.current = effectiveTemplateKey;
    }
    if (refreshKey !== null && refreshKey !== prevRefreshKeyRef.current) {
      prevRefreshKeyRef.current = refreshKey;
    }
    if (resolvedScenarioId && resolvedScenarioId !== prevScenarioIdRef.current) {
      prevScenarioIdRef.current = resolvedScenarioId;
    }
    prevPreviewModeRef.current = previewMode;
  }, [effectiveTemplateKey, refreshKey, resolvedScenarioId, previewMode]);

  const webWidgetDisplayName = React.useMemo(
    () => getWebWidgetDisplayName(effectiveTemplateKey ?? null),
    [effectiveTemplateKey]
  );

  // The API may return the subject as HTML (e.g. "<p>text</p><p></p>").
  // Strip tags so the subject card shows plain text, not raw markup.
  const subjectLine = renderedSubject
    ? renderedSubject.replace(/<[^>]*>/g, '').trim() || null
    : null;
  const isTeaserSection = channel === 'Email' && String(effectiveElementKey || '').toUpperCase() === 'TEASER';
  const teaserText = React.useMemo(
    () => (isTeaserSection ? extractEmailTeaserText(renderedHtml || '') : null),
    [isTeaserSection, renderedHtml]
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1, minWidth: 0 }}>
      {!renderRequest && (
        <Box sx={{ padding: 1.5, textAlign: 'center', color: 'text.secondary' }}>
          <Typography variant="body2">Select a template to preview</Typography>
        </Box>
      )}
      {variantName && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 1,
            padding: '6px 12px',
            borderRadius: 1.5,
            border: 1,
            borderColor: 'divider',
            bgcolor: theme => theme.palette.mode === 'dark'
              ? 'rgba(255, 255, 255, 0.05)'
              : 'rgba(0, 0, 0, 0.02)',
            color: 'text.secondary',
            fontSize: '12px',
            fontWeight: 500
          }}
        >
          <Typography component="span" variant="caption">{variantName}:</Typography>
          <Box
            component="span"
            sx={{
              padding: '2px 8px',
              borderRadius: 0.5,
              bgcolor: theme => theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.1)'
                : 'rgba(0, 0, 0, 0.08)',
              fontSize: '11px'
            }}
          >
            {variant?.hierarchy || 'Project'}
          </Box>
          <Box
            component="span"
            sx={{
              padding: '2px 8px',
              borderRadius: 0.5,
              bgcolor: theme => theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.1)'
                : 'rgba(0, 0, 0, 0.08)',
              fontSize: '11px'
            }}
          >
            {variant?.locale || 'en_US'}
          </Box>
          <Box
            component="span"
            sx={{
              padding: '2px 8px',
              borderRadius: 0.5,
              bgcolor: theme => theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.1)'
                : 'rgba(0, 0, 0, 0.08)',
              fontSize: '11px'
            }}
          >
            {variant?.scenario || 'default'}
          </Box>
        </Box>
      )}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          borderRadius: 2,
          bgcolor: theme => theme.palette.mode === 'dark'
            ? 'rgba(255, 255, 255, 0.03)'
            : 'rgba(0, 0, 0, 0.02)',
          padding: 2,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
        {loading && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: 5,
              color: 'text.secondary'
            }}
          >
            <Typography variant="body2">Loading preview...</Typography>
          </Box>
        )}

        {!loading && channel === 'Email' && subjectLine && isFullTemplateSelected && (
          <Card
            sx={{
              marginBottom: 2,
              boxShadow: theme => theme.palette.mode === 'dark'
                ? '0 4px 6px rgba(0,0,0,0.3)'
                : '0 4px 6px rgba(0,0,0,0.1)',
              bgcolor: 'background.paper'
            }}
          >
            <CardHeader
              sx={{ padding: 1.5 }}
              title={
                <Box
                  component="label"
                  htmlFor={`subject-line-preview-${variantName || 'main'}`}
                sx={{
                    fontSize: '12px',
                    fontWeight: 600,
                    color: 'text.secondary',
                  display: 'block',
                  marginBottom: 0.5
                }}
              >
                Subject Line
                </Box>
              }
              subheader={
                <Typography
                  id={`subject-line-preview-${variantName || 'main'}`}
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    fontSize: '14px',
                    margin: 0
                  }}
                >
                {subjectLine}
              </Typography>
              }
            />
          </Card>
        )}

        {!loading && isTeaserSection && (
          <Card
            sx={{
              margin: '0 auto',
              width: '100%',
              maxWidth: `${desktopPreviewWidth}px`,
              boxShadow: theme => theme.palette.mode === 'dark'
                ? '0 4px 6px rgba(0,0,0,0.3)'
                : '0 4px 6px rgba(0,0,0,0.1)',
              bgcolor: 'background.paper'
            }}
          >
            <CardHeader
              sx={{ padding: 1.5 }}
              title={
                <Box
                  sx={{
                    fontSize: '12px',
                    fontWeight: 600,
                    color: 'text.secondary',
                    display: 'block',
                    marginBottom: 0.5
                  }}
                >
                  Email Preview
                </Box>
              }
              subheader={
                <Box
                  sx={{
                    marginTop: 0.5,
                    padding: '12px 14px',
                    borderRadius: 1.5,
                    border: '1px dashed',
                    borderColor: 'divider',
                    bgcolor: '#fffbe6',
                    color: 'text.primary',
                    fontSize: '14px',
                    lineHeight: 1.5
                  }}
                >
                  {teaserText || 'No email preview text found in the rendered email.'}
                </Box>
              }
            />
          </Card>
        )}

        {/* Web widget path: direct render (no iframe, same as WidgetsSdkDemoPage) */}
        {!loading && !isTeaserSection && channel === 'Web' && isWebWidgetTemplate ? (
          previewMode === 'mobile' ? (
            <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
              <MobilePreview showPhoneFrame>
                <WidgetErrorBoundary>
                  {initError && (
                    <Box sx={{ padding: 2, textAlign: 'center', color: 'error.main' }}>
                      <Typography variant="body2">Error: {initError}</Typography>
                    </Box>
                  )}
                  {!initError && (
                    <WebWidgetMobileIframe
                      sdkScriptUrl={sdkScriptUrl}
                      scenarioId={resolvedScenarioId ?? ''}
                      widgetTag={webMobileWidgetTag}
                      iframeInitConfig={getIframeInitConfig(resolvedScenarioId ?? undefined)}
                      displayName={webWidgetDisplayName}
                      isSdkInitializing={isInitializing}
                      isSdkReady={isReady}
                    />
                  )}
                </WidgetErrorBoundary>
              </MobilePreview>
            </Box>
          ) : (
            <WidgetErrorBoundary>
              <Box
                sx={{
                  margin: '0 auto',
                  bgcolor: 'background.paper',
                  boxShadow: theme => theme.palette.mode === 'dark'
                    ? '0 4px 6px rgba(0,0,0,0.3)'
                    : '0 4px 6px rgba(0,0,0,0.1)',
                  overflow: 'visible',
                  borderRadius: 2,
                  width: `${getWebWidgetDesktopWidth(effectiveTemplateKey ?? null)}px`,
                  minWidth: `${getWebWidgetDesktopWidth(effectiveTemplateKey ?? null)}px`,
                  maxWidth: '100%',
                  minHeight: 400,
                  padding: 2
                }}
              >
                <div
                  ref={widgetContainerRef}
                  id={widgetContainerDomId}
                  style={{
                    width: '100%',
                    height: '100%',
                    minHeight: '400px'
                  }}
                />
                {isInitializing && (
                  <Box sx={{ padding: 2, textAlign: 'center', color: 'text.secondary' }}>
                    <Typography variant="body2">Initializing {webWidgetDisplayName}…</Typography>
                  </Box>
                )}
                {initError && (
                  <Box sx={{ padding: 2, textAlign: 'center', color: 'error.main' }}>
                    <Typography variant="body2">Error: {initError}</Typography>
                  </Box>
                )}
                {!isInitializing && !initError && !isReady && (
                  <Box sx={{ padding: 2, textAlign: 'center', color: 'text.secondary' }}>
                    <Typography variant="body2">Loading {webWidgetDisplayName}…</Typography>
                  </Box>
                )}
              </Box>
            </WidgetErrorBoundary>
          )
        ) : !loading && !isTeaserSection && channel === 'HER' && herPreviewUrl ? (
          // Render HER preview in iframe
          <Box
            sx={{
              width: '100%',
              height: '100%',
              minHeight: '600px',
              bgcolor: 'background.paper',
              borderRadius: 2,
              overflow: 'hidden'
            }}
          >
            <iframe
              src={herPreviewUrl}
              title="HER Preview"
              style={{
                width: '100%',
                height: '100%',
                minHeight: '600px',
                border: 'none',
                backgroundColor: '#ffffff'
              }}
            />
          </Box>
        ) : !loading && !isTeaserSection && renderedHtml ? (
          // Render HTML from API - apply mobile or desktop styling based on previewMode
          previewMode === 'mobile' && channel !== 'HER' ? (
            <Box sx={{ flex: 1, minHeight: 500, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <EmailPreviewErrorBoundary>
                <EmailMobilePreview html={renderedHtml || ''} />
              </EmailPreviewErrorBoundary>
            </Box>
          ) : (
            // Desktop preview — rendered in an iframe so global app CSS
            // (e.g. `* { font-family: Inter !important }`) cannot override the
            // email's inline font declarations, which would cause text to reflow
            // differently from what production email clients render.
            <Box
              sx={{
                margin: '0 auto',
                bgcolor: 'background.paper',
                boxShadow: theme => theme.palette.mode === 'dark'
                  ? '0 4px 6px rgba(0,0,0,0.3)'
                  : '0 4px 6px rgba(0,0,0,0.1)',
                overflow: 'hidden',
                borderRadius: 2,
                ...(channel === 'Web'
                  ? { minWidth: '1152px' }
                  : { maxWidth: `${desktopPreviewWidth}px` })
              }}
            >
              <EmailPreviewErrorBoundary>
                <EmailDesktopPreview html={renderedHtml || ''} />
              </EmailPreviewErrorBoundary>
            </Box>
          )
        ) : !loading && !isTeaserSection && (
          // Show message if no rendered HTML available
          <Box sx={{ padding: 1.5, textAlign: 'center', color: 'text.secondary' }}>
            <Typography variant="body2">No preview available</Typography>
          </Box>
        )}
        </Box>
      </Box>
    </Box>
  );
};
